import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { api } from '@/lib/api';
import { CheckCircle2, XCircle, Mail, Phone, Clock, UserPlus } from 'lucide-react';

interface PendingUser {
  id: string;
  name: string;
  email: string;
  phoneNumber: string | null;
  role: string;
  status: string;
  isApproved: boolean;
  createdAt: string;
}

function timeAgo(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffMin = Math.floor((now - then) / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin} minute${diffMin === 1 ? '' : 's'} ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hour${diffHr === 1 ? '' : 's'} ago`;
  const diffDay = Math.floor(diffHr / 24);
  return `${diffDay} day${diffDay === 1 ? '' : 's'} ago`;
}

export const PendingApprovalsPage: React.FC = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  const { data: pendingUsers = [], isLoading, isError } = useQuery<PendingUser[]>({
    queryKey: ['pending-users'],
    queryFn: api.getPendingUsers,
    refetchInterval: 60000,
  });

  const approveMutation = useMutation({
    mutationFn: ({ id, generatePasswordReset }: { id: string; generatePasswordReset: boolean }) =>
      api.approveUser(id, { generatePasswordReset }),
    onSuccess: (data, variables) => {
      const user = data.user || {};
      toast({
        title: data.alreadyApproved ? 'Already approved' : 'Approved',
        description: data.alreadyApproved
          ? `${user.email || 'User'} was already approved.`
          : `Welcome email sent to ${user.email || 'the user'}.`,
      });
      queryClient.invalidateQueries({ queryKey: ['pending-users'] });
    },
    onError: (err: any) => {
      toast({ title: 'Failed to approve', description: err.message, variant: 'destructive' });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => api.rejectUser(id, reason),
    onSuccess: (data) => {
      toast({
        title: 'Rejected',
        description: `Signup for ${data.deletedUser?.email || 'user'} was removed.`,
      });
      setRejectingId(null);
      setRejectReason('');
      queryClient.invalidateQueries({ queryKey: ['pending-users'] });
    },
    onError: (err: any) => {
      toast({ title: 'Failed to reject', description: err.message, variant: 'destructive' });
    },
  });

  const handleApprove = (user: PendingUser) => {
    approveMutation.mutate({ id: user.id, generatePasswordReset: false });
  };

  const handleConfirmReject = (id: string) => {
    rejectMutation.mutate({ id, reason: rejectReason.trim() || 'No reason provided' });
  };

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
              <UserPlus className="h-6 w-6 text-blue-600" />
              Pending Approvals
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Self-service signups waiting for admin review. Approve to send a welcome email, or reject to remove.
            </p>
          </div>
          <Badge variant="secondary" className="text-sm">
            {pendingUsers.length} pending
          </Badge>
        </div>

        {isLoading && (
          <Card>
            <CardContent className="p-6 text-sm text-gray-500">Loading pending signups…</CardContent>
          </Card>
        )}

        {isError && (
          <Card>
            <CardContent className="p-6 text-sm text-red-600">Failed to load pending signups.</CardContent>
          </Card>
        )}

        {!isLoading && !isError && pendingUsers.length === 0 && (
          <Card>
            <CardContent className="p-12 text-center">
              <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-3" />
              <p className="text-gray-700 font-medium">No pending approvals — you're all caught up.</p>
              <p className="text-sm text-gray-500 mt-2">
                New self-service signups will appear here.
              </p>
            </CardContent>
          </Card>
        )}

        <div className="space-y-3">
          {pendingUsers.map((user) => (
            <Card key={user.id} className="bg-white">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-semibold flex-shrink-0">
                      {user.name?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                    <div className="min-w-0">
                      <CardTitle className="text-base font-semibold text-gray-900 truncate">
                        {user.name}
                      </CardTitle>
                      <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500 mt-1">
                        <span className="flex items-center gap-1">
                          <Mail className="h-3 w-3" />
                          {user.email}
                        </span>
                        {user.phoneNumber && (
                          <span className="flex items-center gap-1">
                            <Phone className="h-3 w-3" />
                            {user.phoneNumber}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {timeAgo(user.createdAt)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button
                      onClick={() => handleApprove(user)}
                      disabled={approveMutation.isPending}
                      className="bg-green-600 hover:bg-green-700 text-white"
                      size="sm"
                    >
                      <CheckCircle2 className="h-4 w-4 mr-1" />
                      Approve
                    </Button>
                    <Button
                      onClick={() => setRejectingId(user.id)}
                      disabled={rejectMutation.isPending}
                      variant="outline"
                      className="border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                      size="sm"
                    >
                      <XCircle className="h-4 w-4 mr-1" />
                      Reject
                    </Button>
                  </div>
                </div>
              </CardHeader>
              {rejectingId === user.id && (
                <CardContent className="pt-0 border-t border-gray-100">
                  <div className="pt-3 space-y-2">
                    <label htmlFor={`reject-reason-${user.id}`} className="text-sm font-medium text-gray-700">
                      Reason for rejection (optional)
                    </label>
                    <textarea
                      id={`reject-reason-${user.id}`}
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      placeholder="e.g. Not a team member, suspicious signup, etc."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      rows={2}
                    />
                    <div className="flex justify-end gap-2 pt-1">
                      <Button
                        onClick={() => {
                          setRejectingId(null);
                          setRejectReason('');
                        }}
                        variant="outline"
                        size="sm"
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={() => handleConfirmReject(user.id)}
                        disabled={rejectMutation.isPending}
                        className="bg-red-600 hover:bg-red-700 text-white"
                        size="sm"
                      >
                        Confirm Reject
                      </Button>
                    </div>
                  </div>
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default PendingApprovalsPage;
